import { billingTrpcContract } from "@amtana/billing-contract";
import type {
  AddSeatsInput,
  AwakeHours,
  BillingDetails,
  BillingOverview,
  CancelInput,
  CheckoutSession,
  CreditHistory,
  InvoiceDocument,
  InvoiceDocumentInput,
  ModelTurnCredits,
  Invoices,
  OfferInput,
  PlanLimits,
  PortalSession,
  SetAutoRechargeInput,
  SimulateInput,
  StartCheckoutInput,
  Subscription,
  PaymentOptions,
  PurchaseKind,
} from "@amtana/billing-contract";
import {
  lentComponent,
  setSearchParam,
  useContractMutation,
  useContractQuery,
  useFlag,
  useInvalidateContract,
} from "@amtana/browser";
import type { LentToken, MutationResult, QueryResult } from "@amtana/browser";

// Billing for every browser module: the organization's plan, balances and
// limits (ADR-003), the subscription, invoices, the payment provider's
// checkout and portal, and the billing panel My Account shows (lent by the
// billing module). Every member may read the balance; only an admin may
// change the plan, buy credits or read invoices (the permission door).

export function useBillingOverview(
  options: { readonly optional?: boolean; readonly enabled?: boolean } = {},
): QueryResult<BillingOverview> {
  return useContractQuery(billingTrpcContract, "overview", {
    input: {},
    optional: options.optional ?? false,
    enabled: options.enabled ?? true,
  });
}

export function usePlanLimits(): QueryResult<PlanLimits> {
  return useContractQuery(billingTrpcContract, "limits", { input: {} });
}

/** Every credit spent or granted, newest first, each naming the member who caused it. */
export function useCreditHistory(): QueryResult<CreditHistory> {
  return useContractQuery(billingTrpcContract, "creditHistory", { input: {} });
}

/** The remote agent's awake hours this month (ADR-023 §13); behind `remote-agents`. */
export function useAwakeHours(): QueryResult<AwakeHours> {
  const on = useFlag("remote-agents") === true;
  return useContractQuery(billingTrpcContract, "awakeHours", { input: {}, enabled: on, optional: true });
}

/** What each turn on our model drew from the AI credits (D676); behind `remote-agents`. `live`
 * reads again every few seconds while a turn runs. */
export function useModelTurnCredits(
  turnIds: readonly string[],
  options: { readonly live?: boolean } = {},
): QueryResult<ModelTurnCredits> {
  const on = useFlag("remote-agents") === true && turnIds.length > 0;
  return useContractQuery(billingTrpcContract, "modelTurnCredits", {
    input: { turnIds: [...turnIds] },
    enabled: on,
    optional: true,
    ...(options.live ? { refetchInterval: 3_000 } : {}),
  });
}

export function useSubscription(): QueryResult<Subscription> {
  return useContractQuery(billingTrpcContract, "subscription", { input: {} });
}

/** The providers a member may pay a plan or a credit pack with, the default first (ADR-017 §5). */
export function usePaymentOptions(kind: PurchaseKind): QueryResult<PaymentOptions> {
  return useContractQuery(billingTrpcContract, "paymentOptions", { input: { kind } });
}

export function useInvoices(): QueryResult<Invoices> {
  return useContractQuery(billingTrpcContract, "invoices", { input: {} });
}

function useWrite<TInput, TOutput>(
  name:
    | "simulate"
    | "startCheckout"
    | "portalLink"
    | "cancel"
    | "uncancel"
    | "acceptOffer"
    | "unfreeze"
    | "addSeats"
    | "setAutoRecharge"
    | "invoiceDocument"
    | "setBillingDetails",
): MutationResult<TOutput, TInput> {
  const mutation = useContractMutation(billingTrpcContract, name);
  const invalidate = useInvalidateContract();
  // The contract types `name`'s input and output; the hook restates them for consumers.
  const typed = mutation as unknown as MutationResult<TOutput, TInput>;
  // A plan or a balance changes what every screen may do, so every read refreshes.
  return { ...typed, run: (input: TInput) => typed.mutateAsync(input).finally(() => void invalidate()) };
}

/** Simulation control: put the organization on any plan and status; every read refreshes. */
export const useSimulateBilling = (): MutationResult<BillingOverview, SimulateInput> => useWrite("simulate");
/** Starts a checkout with the payment provider; the plan or the credits land once its webhook confirms the charge. */
export const useStartCheckout = (): MutationResult<CheckoutSession, StartCheckoutInput> =>
  useWrite("startCheckout");
export const usePortalLink = (): MutationResult<PortalSession, Record<string, never>> =>
  useWrite("portalLink");
export const useCancelSubscription = (): MutationResult<Subscription, CancelInput> => useWrite("cancel");
export const useUncancel = (): MutationResult<Subscription, Record<string, never>> => useWrite("uncancel");
export const useAcceptOffer = (): MutationResult<Subscription, OfferInput> => useWrite("acceptOffer");
export const useUnfreeze = (): MutationResult<Subscription, Record<string, never>> => useWrite("unfreeze");
/** Raises the organization's seat cap (S3.07); refused on the free plan. */
export const useAddSeats = (): MutationResult<BillingOverview, AddSeatsInput> => useWrite("addSeats");
/** Turns auto-recharge on or off, with a threshold and a credit-pack size (S5.05). */
export const useSetAutoRecharge = (): MutationResult<BillingOverview, SetAutoRechargeInput> =>
  useWrite("setAutoRecharge");
/** Fetches one invoice's downloadable document on demand (S5.07). */
export const useInvoiceDocument = (): MutationResult<InvoiceDocument, InvoiceDocumentInput> =>
  useWrite("invoiceDocument");
/** Sets the name, email, address and tax id the invoice document shows (S5.07). */
export const useSetBillingDetails = (): MutationResult<BillingDetails, BillingDetails> =>
  useWrite("setBillingDetails");

/** The organization's seats (S3.07, S5.12): included, used, bought, and the way to add one. The
 * Add-ons page renders it (epic 40, D1101); billing decides what it shows and sells. */
export const SeatsPanel: LentToken<Record<string, never>> = lentComponent<Record<string, never>>(
  "billing",
  "SeatsPanel",
);

/**
 * The credit packs dialog, mounted once by the app shell: it opens on any page
 * when the URL says `?buy=credits` or `?buy=ai-credits`, so a menu item or a
 * button anywhere can sell credits without leaving the page.
 */
export const BuyCreditsHost: LentToken<Record<string, never>> = lentComponent<Record<string, never>>(
  "billing",
  "BuyCreditsHost",
);

/** The pack kind a checkout buys: billing's own `CreditPackKind`, or an awake-hours pack
 * (ADR-023 §13), restated because a client imports only its own module's contract. */
type CreditPackKind = "analysis" | "ai" | "hours";

/** Above the Agents area (ADR-023 §13): "Get Remote agent" without the add-on, or the hours packs
 * once this month's hours are used; nothing otherwise. Billing decides which. */
export const RemoteAgentNotice: LentToken<Record<string, never>> = lentComponent<Record<string, never>>(
  "billing",
  "RemoteAgentNotice",
);

/** Opens the credit packs over the current page; the shell's BuyCreditsHost shows them. */
export function openCreditPacks(kind: CreditPackKind): void {
  setSearchParam(CREDIT_PACKS_PARAM, kind);
}

/** The URL parameter the host reads; only billing names it. */
export const CREDIT_PACKS_PARAM = "buy";

/** The credit packs, as a link: for pages that send people to buy (`/credits?buy=credits`). */
export function creditPacksHref(path: string, kind: CreditPackKind): string {
  return `${path}?${CREDIT_PACKS_PARAM}=${kind}`;
}
